// Reviewing an imported patient, from wherever it is needed: opening its record (the dashboard opens the review
// straight away), choosing one for a task, the review list, or a save the server refused
// because the patient still needs reviewing.
//
// open() shows the review and resolves with the patient to carry on with - the existing patient it was merged
// into, or the same patient once confirmed - and rejects when it is closed without finishing. openUncreated() does
// the same for a patient the import couldn't create, resolving with the patient its records were added to.
app.service("PatientImportReview", [
	"$q",
	"$mdDialog",
	"toast",
	"CurrentPatient",
	"PatientAPI",
	"PatientImportAPI",
	function ($q, $mdDialog, toast, CurrentPatient, PatientAPI, PatientImportAPI) {
		// While a review is showing, asking again (say, two refused saves at once) gives the same one.
		let showing = null;

		// The patient a refused request was about: from its body (a task or appointment names it), or its URL
		// (/patients/12/..., /patient/12/...).
		function patientIdFrom(config) {
			let data = config && config.data;
			if (typeof data === "string") {
				try {
					data = JSON.parse(data);
				} catch (e) {
					data = null;
				}
			}
			const fromBody = data && (data.patient_id || data.patientId);
			if (fromBody) {
				return Number(fromBody);
			}
			const fromUrl = /\/patients?\/(\d+)(\/|\?|$)/.exec((config && config.url) || "");
			return fromUrl ? Number(fromUrl[1]) : null;
		}

		function notify(message, className) {
			toast.create({
				message: message,
				className: className || "alert-info",
				timeout: 8000,
				dismissible: true,
			});
		}

		const service = {
			open: function (patientId, options) {
				if (showing) {
					return showing;
				}
				// Loaded before the dialog shows, so it opens on its first step at that step's width rather than
				// opening narrow and then widening. Shown anyway when loading fails, to say so.
				showing = $q
					.all([PatientImportAPI.getReview(patientId), PatientAPI.getPatient(patientId)])
					.then(
						(responses) => ({ review: responses[0].data, patient: responses[1].data }),
						() => ({ review: null, patient: null })
					)
					.then(function (loaded) {
						if (loaded.review && !loaded.review.needs_review) {
							// Reviewed already, by someone else or a moment ago: nothing to do.
							return loaded.patient;
						}
						return $mdDialog
							.show({
								controller: "patientImportReviewController",
								templateUrl: "app/src/js/components/patientImport/patientImportReview.html",
								parent: angular.element(document.body),
								clickOutsideToClose: false,
								// Usually over another dialog (a task, the review list), which stays open underneath.
								multiple: true,
								locals: {
									patientId: patientId,
									options: options || {},
									review: loaded.review,
									patient: loaded.patient,
								},
							})
							.then(function (result) {
								return result.patient;
							});
					})
					.finally(function () {
						showing = null;
					});
				return showing;
			},

			// A patient the import couldn't create: their records are added to a patient already in CatTrax or to a new
			// one. Loaded before the dialog shows, as open() does, and shown anyway when loading fails, to say so.
			openUncreated: function (uncreatedId) {
				return PatientImportAPI.getUncreated(uncreatedId)
					.then(
						(resp) => resp.data,
						() => null
					)
					.then(function (uncreated) {
						return $mdDialog
							.show({
								controller: "patientImportUncreatedController",
								templateUrl: "app/src/js/components/patientImport/patientImportUncreated.html",
								parent: angular.element(document.body),
								clickOutsideToClose: false,
								// Over the review list, which stays open underneath.
								multiple: true,
								locals: {
									uncreatedId: uncreatedId,
									uncreated: uncreated,
								},
							})
							.then(function (result) {
								return result.patient;
							});
					});
			},

			// The server refused a request: 409 PATIENT_IMPORT_REVIEW_REQUIRED or PATIENT_MERGED.
			handleRefusal: function (resp) {
				const patientId = patientIdFrom(resp.config);
				if (resp.data.code === "PATIENT_MERGED") {
					if (patientId) {
						service.followMerge(patientId);
					} else {
						notify("This patient was merged into another patient's record.", "alert-warning");
					}
					return;
				}

				if (!patientId) {
					notify(resp.data.message || "This patient needs reviewing before anything else can be added to them.", "alert-warning");
					return;
				}
				service.open(patientId, { refused: resp.data.message }).catch(angular.noop);
			},

			// A link to a patient that was merged away (a recent patient, an open record): open where it went.
			followMerge: function (patientId) {
				return PatientImportAPI.getMergedInto(patientId)
					.then(function (resp) {
						const mergedInto = resp.data && resp.data.merged_into_patient_id;
						if (!mergedInto) {
							return;
						}
						if (CurrentPatient.patient && CurrentPatient.patient.id === patientId) {
							return PatientAPI.getPatient(mergedInto).then(function (patientResp) {
								CurrentPatient.setPatient(patientResp.data);
								notify("This patient was merged into another record, which is now open.", "alert-info");
							});
						}
						notify("This patient was merged into another patient's record. Search for them again.", "alert-warning");
					})
					.catch(angular.noop);
			},
		};

		return service;
	},
]);
